// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { post } from '@tls/auth';
import { HubContext } from 'components/common/emitter/HubContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  EvaluateQCProgramCompleteMessage,
  EvaluateQCProgramProgressMessage,
} from '@tls/sw91-communication/types/com.api_predictor';
import { ProjectVersion } from 'model/ProjectMetaMessageExtensions';
import { clearEvaluationProgressCache } from 'store/reducers/projectEvaluationSlice';
import { useAppDispatch } from 'store/hooks';
import useIsQC from './useIsQC';
import { QCProgram } from '@tls/sw91_ui_templates/lib/models/project-models';

export function useQCEvaluateApi(projectId: string) {
  const [progress, setProgress] = useState<Record<ProjectVersion, number>>({});
  const dispatch = useAppDispatch();
  const isQC = useIsQC();

  const [evaluationResults, setEvaluationResults] = useState<Record<ProjectVersion, EvaluateQCProgramCompleteMessage>>(
    {}
  );

  const { events } = useContext(HubContext);

  // Listen to events as soon as the exportingModel was set
  useEffect(() => {
    if (!isQC) return;
    function onProgressUpdate(message: EvaluateQCProgramProgressMessage) {
      console.log('QCProgressUpdate', message);
      setProgress(progress => {
        if (message.strategyVersion) {
          return { ...progress, [message.strategyVersion]: message.progress ?? 0 };
        }
        return progress;
      });
    }
    function onProgressError(message: EvaluateQCProgramCompleteMessage) {
      console.log('QCProgressError', message);
      setProgress(progress => {
        if (message.strategyVersion) {
          return { ...progress, [message.strategyVersion]: 1 };
        }
        return progress;
      });
    }

    function onProgressCompleted(message: EvaluateQCProgramCompleteMessage) {
      console.log('Evaluation completed', message);
      setProgress(progress => {
        if (message.strategyVersion) {
          return { ...progress, [message.strategyVersion]: 1 };
        }
        return progress;
      });
      setEvaluationResults(results => {
        if (message.strategyVersion) {
          return { ...results, [message.strategyVersion]: message };
        }
        return results;
      });
    }
    console.log('Register on QCEvalution');

    events.on('onQCProgramEvaluationProgress', onProgressUpdate);
    events.on('onQCProgramEvaluationFailed', onProgressError);
    events.on('onQCProgramEvaluationCompleted', onProgressCompleted);

    return () => {
      events.off('onQCProgramEvaluationProgress', onProgressUpdate);
      events.off('onQCProgramEvaluationFailed', onProgressError);
      events.off('onQCProgramEvaluationCompleted', onProgressCompleted);
      if (isQC) dispatch(clearEvaluationProgressCache());
    };
  }, [events, dispatch, isQC]);

  const triggerQCEvaluation = useCallback(
    async (qcProgram: QCProgram, translation: any, version: string) => {
      setProgress({});
      const body = {
        qcpProgramJson: qcProgram,
        qcpTranslations: translation,
        language: 'en',
        version,
      };
      await post(`projects/${projectId}/models/qcpevaluation/trigger`, body);
    },
    [projectId]
  );

  return {
    triggerQCEvaluation,
    evaluationResults,
    qcEvaluationProgress: progress,
  };
}
